import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { LOGO } from "../data/site";

const links = [
  { to: "/", label: "Home" },
  { to: "/programs", label: "Programs" },
  { to: "/about", label: "About" },
  { to: "/gallery", label: "Gallery" },
  { to: "/admissions", label: "Admissions" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-cream/85 backdrop-blur-xl border-b border-navy/10 py-3" : "py-5"
      }`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center group" data-testid="logo-link">
          <img src={LOGO.text} alt="THE THINKSTITUTE" className="h-10 sm:h-12 w-auto group-hover:scale-105 transition-transform" />
        </Link>

        <nav className="hidden md:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                  isActive
                    ? "bg-navy text-cream"
                    : "text-navy/80 hover:text-marigold"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/admissions"
            data-testid="nav-cta"
            className="ml-3 px-5 py-2.5 rounded-full bg-marigold text-white text-sm font-bold shadow-[0_6px_0_0_#c85c14] hover:translate-y-0.5 hover:shadow-[0_3px_0_0_#c85c14] transition-all"
          >
            Book Free Consult
          </Link>
        </nav>

        <button
          className="md:hidden text-navy p-2"
          onClick={() => setOpen(true)}
          aria-label="Open menu"
          data-testid="mobile-menu-open"
        >
          <Menu size={26} />
        </button>
      </div>
    </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-navy text-cream md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex justify-between items-center px-5 py-5">
              <span className="inline-flex bg-white rounded-xl px-3 py-1.5">
                <img src={LOGO.text} alt="THE THINKSTITUTE" className="h-5 w-auto" />
              </span>
              <button onClick={() => setOpen(false)} aria-label="Close menu" data-testid="mobile-menu-close">
                <X size={28} />
              </button>
            </div>
            <div className="flex flex-col gap-2 px-6 mt-8">
              {links.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i }}
                >
                  <NavLink
                    to={l.to}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                    className="block font-display font-bold text-4xl py-2 hover:text-marigold transition-colors"
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <Link
                to="/admissions"
                onClick={() => setOpen(false)}
                className="mt-8 text-center px-6 py-4 rounded-full bg-marigold text-white font-bold text-lg"
                data-testid="mobile-nav-cta"
              >
                Book a Free Consultation
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
